import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import {
  Button,
  ErrorNote,
  Field,
  Panel,
  errorMessage,
  fieldIssuesMessage,
  inputClass,
} from "@/components/ui";
import { fromDateInput, toDateInput } from "@/lib/format";
import type { Job, JobRemote, SalaryPeriod, Sector } from "@/lib/types";

const MAX_EXPIRY_DAYS = 90;

/** AAAA-MM-DD de hoy + `days`, para los límites del <input type="date">. */
function dateInputOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDateInput(Math.floor(d.getTime() / 1000));
}

interface FormState {
  title: string;
  company: string;
  sector: string;
  location: string;
  remote: JobRemote;
  contractType: string;
  description: string;
  requirements: string;
  salaryMin: string;
  salaryMax: string;
  salaryCurrency: string;
  salaryPeriod: SalaryPeriod;
  expires: string;
  notifyApplications: boolean;
}

function initialState(defaultCompany: string): FormState {
  return {
    title: "",
    company: defaultCompany,
    sector: "red-team",
    location: "",
    remote: "onsite",
    contractType: "",
    description: "",
    requirements: "",
    salaryMin: "",
    salaryMax: "",
    salaryCurrency: "EUR",
    salaryPeriod: "year",
    // Por defecto el máximo permitido: es lo que casi siempre quiere el
    // reclutador, y deja claro cuál es el techo.
    expires: dateInputOffset(MAX_EXPIRY_DAYS),
    // Apagado por defecto, igual que la columna en jobs-db.
    notifyApplications: false,
  };
}

export function JobForm({
  jobId,
  sectors,
  defaultCompany,
  onDone,
  onCancel,
}: {
  jobId: number | null;
  sectors: Sector[];
  defaultCompany: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState<FormState>(() => initialState(defaultCompany));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(jobId !== null);

  useEffect(() => {
    if (jobId === null) return;
    let alive = true;
    (async () => {
      try {
        const j = await api<Job>(`/api/recruiter/jobs/${jobId}`);
        if (!alive) return;
        setF({
          title: j.title,
          company: j.company,
          sector: j.sector,
          location: j.location ?? "",
          remote: j.remote,
          contractType: j.contractType ?? "",
          description: j.description,
          requirements: j.requirements,
          salaryMin: j.salaryMin?.toString() ?? "",
          salaryMax: j.salaryMax?.toString() ?? "",
          salaryCurrency: j.salaryCurrency,
          salaryPeriod: j.salaryPeriod,
          // Si la oferta ya caducó, el <input> rechazaría una fecha por
          // debajo de su `min`. Se propone el máximo: guardar reactiva.
          expires:
            (j.expiresAt ?? 0) * 1000 > Date.now()
              ? toDateInput(j.expiresAt)
              : dateInputOffset(MAX_EXPIRY_DAYS),
          notifyApplications: j.notifyApplications,
        });
      } catch (e) {
        setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  async function save() {
    if (busy) return;
    setError(null);
    const expiresAt = fromDateInput(f.expires);
    if (!expiresAt) {
      setError("Indica una fecha de caducidad.");
      return;
    }
    const num = (s: string) => (s.trim() === "" ? null : Number(s));
    const body = {
      title: f.title.trim(),
      company: f.company.trim(),
      sector: f.sector,
      location: f.location.trim() || undefined,
      remote: f.remote,
      contractType: f.contractType.trim() || undefined,
      description: f.description,
      requirements: f.requirements,
      salaryMin: num(f.salaryMin),
      salaryMax: num(f.salaryMax),
      salaryCurrency: f.salaryCurrency.toUpperCase(),
      salaryPeriod: f.salaryPeriod,
      expiresAt,
      notifyApplications: f.notifyApplications,
    };
    setBusy(true);
    try {
      if (jobId === null) {
        await api("/api/recruiter/jobs", { method: "POST", body: JSON.stringify(body) });
      } else {
        await api(`/api/recruiter/jobs/${jobId}`, { method: "PUT", body: JSON.stringify(body) });
      }
      onDone();
    } catch (e) {
      // La API devuelve `issues` con el campo exacto que falla; mostrarlo en
      // vez de un "revisa los campos" genérico que no marca ninguno.
      if (e instanceof ApiError) {
        setError(fieldIssuesMessage(e.payload) ?? errorMessage(e.code));
      } else {
        setError(errorMessage("unknown"));
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="text-sm text-mute">Cargando oferta…</p>;

  return (
    <Panel>
      <h2 className="mb-4 font-mono text-lg">
        {jobId === null ? "Nueva oferta" : "Editar oferta"}
      </h2>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {/* Los `required` / `minLength` / `step` replican los mínimos del
            esquema Zod del servidor, para que el navegador avise ANTES de
            enviar en vez de que el usuario coma un 400. La validación real
            sigue estando en la API — esto solo evita el viaje inútil. */}
        <Field label="título del puesto" hint="Mínimo 3 caracteres.">
          <input
            className={inputClass}
            required
            minLength={3}
            maxLength={160}
            value={f.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="quién publica" hint="Por defecto, tu empresa.">
            <input className={inputClass} value={f.company} onChange={(e) => set("company", e.target.value)} />
          </Field>
          <Field label="sector">
            <select className={inputClass} value={f.sector} onChange={(e) => set("sector", e.target.value)}>
              {sectors.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="ubicación">
            <input className={inputClass} value={f.location} onChange={(e) => set("location", e.target.value)} />
          </Field>
          <Field label="modalidad">
            <select
              className={inputClass}
              value={f.remote}
              onChange={(e) => set("remote", e.target.value as JobRemote)}
            >
              <option value="onsite">Presencial</option>
              <option value="hybrid">Híbrido</option>
              <option value="remote">En remoto</option>
            </select>
          </Field>
          <Field label="tipo de contrato">
            <input
              className={inputClass}
              placeholder="Indefinido, freelance…"
              value={f.contractType}
              onChange={(e) => set("contractType", e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="descripción del puesto"
          hint={`Mínimo 20 caracteres (llevas ${f.description.trim().length}).`}
        >
          <textarea
            className={`${inputClass} min-h-40`}
            required
            minLength={20}
            maxLength={20000}
            value={f.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <Field
          label="requisitos"
          hint={`Mínimo 10 caracteres (llevas ${f.requirements.trim().length}).`}
        >
          <textarea
            className={`${inputClass} min-h-32`}
            required
            minLength={10}
            maxLength={20000}
            value={f.requirements}
            onChange={(e) => set("requirements", e.target.value)}
          />
        </Field>

        <fieldset className="rounded border border-line p-3">
          <legend className="px-1 font-mono text-xs uppercase tracking-wider text-mute">
            rango salarial
          </legend>
          <div className="grid gap-4 sm:grid-cols-4">
            {/* step=1: la columna es entera, y un 45000.5 lo rechaza el
                servidor. Mejor que lo frene el navegador. */}
            <Field label="mínimo">
              <input
                className={inputClass}
                type="number"
                min={0}
                step={1}
                value={f.salaryMin}
                onChange={(e) => set("salaryMin", e.target.value)}
              />
            </Field>
            <Field label="máximo">
              <input
                className={inputClass}
                type="number"
                min={0}
                step={1}
                value={f.salaryMax}
                onChange={(e) => set("salaryMax", e.target.value)}
              />
            </Field>
            <Field label="moneda" hint="Código ISO de 3 letras.">
              <input
                className={inputClass}
                required
                minLength={3}
                maxLength={3}
                value={f.salaryCurrency}
                onChange={(e) => set("salaryCurrency", e.target.value)}
              />
            </Field>
            <Field label="periodo">
              <select
                className={inputClass}
                value={f.salaryPeriod}
                onChange={(e) => set("salaryPeriod", e.target.value as SalaryPeriod)}
              >
                <option value="year">Anual</option>
                <option value="month">Mensual</option>
                <option value="hour">Por hora</option>
              </select>
            </Field>
          </div>
          <p className="mt-2 text-xs text-mute">
            Puedes dejar uno de los dos en blanco para publicar un rango abierto.
          </p>
        </fieldset>

        <Field
          label="caduca el"
          hint={`Máximo ${MAX_EXPIRY_DAYS} días (3 meses). Al caducar, la oferta deja de verse hasta que la reactives.`}
        >
          <input
            className={inputClass}
            type="date"
            value={f.expires}
            // El tope también se valida en el servidor: esto solo evita el
            // viaje de ida y vuelta con un error previsible.
            min={dateInputOffset(1)}
            max={dateInputOffset(MAX_EXPIRY_DAYS)}
            onChange={(e) => set("expires", e.target.value)}
          />
        </Field>

        <label className="flex cursor-pointer items-start gap-3 rounded border border-line p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            checked={f.notifyApplications}
            onChange={(e) => set("notifyApplications", e.target.checked)}
          />
          <span>
            <span className="block font-mono text-sm">
              avisarme por correo en cada candidatura
            </span>
            <span className="mt-1 block text-xs text-mute">
              Desactivado por defecto. Una oferta con tirón puede generar decenas de correos al
              día; las candidaturas se guardan igual y las tienes siempre en «candidaturas».
            </span>
          </span>
        </label>

        <ErrorNote>{error}</ErrorNote>

        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>
            {busy ? "guardando…" : jobId === null ? "publicar oferta" : "guardar cambios"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            cancelar
          </Button>
        </div>
      </form>
    </Panel>
  );
}
